/**
 * Haptic Feedback Helper Utilities
 * Standalone functions for use without React hooks
 */

/**
 * Check if haptic feedback is supported
 */
export const isHapticSupported = () => {
  return 'vibrate' in navigator;
};

/**
 * Trigger vibration with pattern
 * @param {number|number[]} pattern - Vibration pattern in milliseconds
 */
export const triggerHaptic = (pattern) => {
  if (!isHapticSupported()) return;
  
  try {
    navigator.vibrate(pattern);
  } catch (error) {
    console.error('Haptic error:', error);
  }
};

/**
 * Pre-defined haptic patterns
 */
export const HapticPatterns = {
  LIGHT: 10,
  MEDIUM: 20,
  HEAVY: 40,
  SUCCESS: [20, 50, 20],
  ERROR: [50, 100, 50, 100, 50],
  WARNING: [30, 100, 30],
  SELECTION: 15,
  NOTIFICATION: [20, 100, 10],
  LONG_PRESS: 100,
};

/**
 * Haptic feedback for common interactions
 */
export const Haptics = {
  // Button clicks
  buttonClick: () => triggerHaptic(HapticPatterns.MEDIUM),
  
  // Links
  linkClick: () => triggerHaptic(HapticPatterns.LIGHT),
  
  // Form interactions
  inputFocus: () => triggerHaptic(HapticPatterns.LIGHT),
  inputBlur: () => triggerHaptic(HapticPatterns.LIGHT),
  formSubmit: () => triggerHaptic(HapticPatterns.SUCCESS),
  formError: () => triggerHaptic(HapticPatterns.ERROR),
  
  // Selections
  checkboxToggle: () => triggerHaptic(HapticPatterns.SELECTION),
  radioSelect: () => triggerHaptic(HapticPatterns.SELECTION),
  tabSelect: () => triggerHaptic(HapticPatterns.MEDIUM),
  
  // Navigation
  navClick: () => triggerHaptic(HapticPatterns.MEDIUM),
  backButton: () => triggerHaptic(HapticPatterns.LIGHT),
  
  // Actions
  deleteAction: () => triggerHaptic(HapticPatterns.HEAVY),
  saveAction: () => triggerHaptic(HapticPatterns.SUCCESS),
  cancelAction: () => triggerHaptic(HapticPatterns.LIGHT),
  
  // Notifications
  notificationReceived: () => triggerHaptic(HapticPatterns.NOTIFICATION),
  alertShown: () => triggerHaptic(HapticPatterns.WARNING),
  
  // Gestures
  swipe: () => triggerHaptic(HapticPatterns.LIGHT),
  longPress: () => triggerHaptic(HapticPatterns.LONG_PRESS),
  pullToRefresh: () => triggerHaptic(HapticPatterns.MEDIUM),
  
  // Custom
  custom: (pattern) => triggerHaptic(pattern),
};

/**
 * Add haptic feedback to DOM element
 * @param {HTMLElement} element - DOM element
 * @param {string} eventType - Event type (click, focus, etc.)
 * @param {number|number[]} pattern - Vibration pattern
 */
export const addHapticToElement = (element, eventType = 'click', pattern = HapticPatterns.MEDIUM) => {
  if (!element || !isHapticSupported()) return;

  const handler = () => triggerHaptic(pattern);
  element.addEventListener(eventType, handler, { passive: true });

  // Return cleanup function
  return () => {
    element.removeEventListener(eventType, handler);
  };
};

/**
 * Add haptic feedback to multiple elements
 * @param {string} selector - CSS selector
 * @param {string} eventType - Event type
 * @param {number|number[]} pattern - Vibration pattern
 */
export const addHapticToElements = (selector, eventType = 'click', pattern = HapticPatterns.MEDIUM) => {
  if (!isHapticSupported()) return;

  const elements = document.querySelectorAll(selector);
  const cleanupFunctions = [];

  elements.forEach(element => {
    const cleanup = addHapticToElement(element, eventType, pattern);
    if (cleanup) cleanupFunctions.push(cleanup);
  });

  // Return cleanup function for all elements
  return () => {
    cleanupFunctions.forEach(cleanup => cleanup());
  };
};

/**
 * Initialize haptic feedback for common interactive elements
 */
export const initializeHaptics = () => {
  if (!isHapticSupported()) {
    console.log('Haptic feedback not supported on this device');
    return;
  }

  // Buttons
  addHapticToElements('button', 'click', HapticPatterns.MEDIUM);
  
  // Links
  addHapticToElements('a', 'click', HapticPatterns.LIGHT);
  
  // Form inputs
  addHapticToElements('input, textarea, select', 'focus', HapticPatterns.LIGHT);
  
  // Checkboxes and radios
  addHapticToElements('input[type="checkbox"], input[type="radio"]', 'change', HapticPatterns.SELECTION);
  
  console.log('Haptic feedback initialized');
};

/**
 * Cancel ongoing vibration
 */
export const cancelHaptic = () => {
  if (isHapticSupported()) {
    navigator.vibrate(0);
  }
};

export default Haptics;

